<template>
  <div class="tw-border-t tw-border-gray-200 tw-bg-gray-50 tw-p-4">
    <!-- Enhanced Input Area -->
    <div class="tw-relative tw-bg-white tw-rounded-lg tw-border tw-border-gray-300 tw-shadow-sm tw-focus-within:tw-ring-2 tw-focus-within:tw-ring-primary-500 tw-focus-within:tw-border-primary-500 tw-transition-all">
      <!-- Textarea -->
      <textarea
        v-model="messageText"
        @keydown.enter.prevent="sendMessage"
        @input="handleInput"
        placeholder="Type your message..."
        class="tw-w-full tw-px-4 tw-py-3 tw-text-sm tw-border-0 tw-rounded-lg tw-resize-none tw-outline-none tw-bg-transparent tw-min-h-[10px] tw-max-h-[120px]"
        rows="1"
        ref="messageInput"
        :disabled="sending"
      ></textarea>

      <!-- Input Actions -->
      <div class="tw-flex tw-items-center tw-px-3 tw-pb-2" :class="showCompanyFeatures ? 'tw-justify-between' : 'tw-justify-end'">
        <!-- Left Actions (company features) -->
        <div v-if="showCompanyFeatures" class="tw-flex tw-items-center tw-gap-1">
          <!-- Schedule Meeting -->
          <button
            @click="openMeetingPopup"
            :disabled="sending"
            class="tw-p-2 tw-text-gray-400 hover:tw-text-primary-500 hover:tw-bg-primary-50 tw-rounded-md tw-transition-all tw-duration-200 tw-flex tw-items-center tw-gap-1"
            title="Schedule a meeting"
          >
            <icon icon="solar:calendar-bold-duotone" class="tw-text-sm" />
            <span class="tw-text-xs tw-hidden sm:tw-inline">Meeting</span>
          </button>

          <!-- Tag Product/Service -->
          <button
            @click="toggleTagPicker"
            :disabled="sending"
            :class="[
              'tw-p-2 tw-rounded-md tw-transition-all tw-duration-200 tw-flex tw-items-center tw-gap-1',
              showTagPicker
                ? 'tw-bg-primary-100 tw-text-primary-600'
                : 'tw-text-gray-400 hover:tw-text-primary-500 hover:tw-bg-primary-50'
            ]"
            title="Tag a product or service"
          >
            <icon icon="solar:tag-bold-duotone" class="tw-text-sm" />
            <span class="tw-text-xs tw-hidden sm:tw-inline">Tag</span>
          </button>
        </div>

        <!-- Right Actions -->
        <div class="tw-flex tw-items-center tw-gap-1">
          <button
            v-if="messageText.trim()"
            @click="clearMessage"
            class="tw-p-2 tw-text-gray-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-rounded-md tw-transition-all tw-duration-200 tw-group"
            title="Clear message"
            :disabled="sending"
          >
            <icon icon="solar:close-circle-bold-duotone" class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform" />
          </button>
          <button
            @click="sendMessage"
            :disabled="!messageText.trim() || sending"
            class="tw-p-2 tw-bg-primary-500 tw-text-white tw-rounded-md hover:tw-bg-primary-600 disabled:tw-bg-gray-300 disabled:tw-cursor-not-allowed tw-transition-all tw-duration-200 tw-flex-shrink-0 tw-group tw-shadow-sm hover:tw-shadow-md"
            title="Send message"
          >
            <icon
              v-if="!sending"
              :icon="messageText.trim() ? 'solar:map-arrow-right-bold-duotone' : 'solar:map-arrow-right-linear'"
              class="tw-text-sm group-hover:tw-scale-110 tw-transition-transform"
            />
            <icon
              v-else
              icon="line-md:loading-alt-loop"
              class="tw-text-sm"
            />
          </button>
        </div>
      </div>

      <!-- Tag Picker Panel -->
      <div v-if="showCompanyFeatures && showTagPicker" class="tw-border-t tw-border-gray-200 tw-px-3 tw-py-2 tw-bg-gray-50 tw-rounded-b-lg">
        <div v-if="tagPickerLoading" class="tw-flex tw-items-center tw-justify-center tw-py-3">
          <icon icon="line-md:loading-twotone-loop" class="tw-text-lg tw-text-primary-500" />
        </div>
        <div v-else>
          <!-- Products -->
          <div v-if="companyProductsList.length > 0" class="tw-mb-2">
            <p class="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1 tw-flex tw-items-center tw-gap-1">
              <icon icon="solar:box-bold-duotone" class="tw-text-xs" /> Products
            </p>
            <div class="tw-flex tw-flex-wrap tw-gap-1">
              <button
                v-for="product in companyProductsList"
                :key="'p-' + product.id"
                @click="appendTag('📦 Product', product.name)"
                class="tw-px-2 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-300 tw-text-gray-700 hover:tw-text-primary-700 tw-transition-all"
              >
                {{ product.name }}
              </button>
            </div>
          </div>

          <!-- Services -->
          <div v-if="companyServicesList.length > 0">
            <p class="tw-text-xs tw-font-medium tw-text-gray-500 tw-mb-1 tw-flex tw-items-center tw-gap-1">
              <icon icon="solar:settings-bold-duotone" class="tw-text-xs" /> Services
            </p>
            <div class="tw-flex tw-flex-wrap tw-gap-1">
              <button
                v-for="service in companyServicesList"
                :key="'s-' + service.id"
                @click="appendTag('🔧 Service', service.name)"
                class="tw-px-2 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-300 tw-text-gray-700 hover:tw-text-primary-700 tw-transition-all"
              >
                {{ service.name }}
              </button>
            </div>
          </div>

          <!-- Empty -->
          <div v-if="companyProductsList.length === 0 && companyServicesList.length === 0" class="tw-py-2 tw-text-center">
            <p class="tw-text-xs tw-text-gray-400">No products or services found for this company.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Quick Reply Suggestions -->
    <div v-if="!messageText.trim() && !sending" class="tw-flex tw-items-center tw-gap-2 tw-mt-3 tw-flex-wrap">
      <button
        v-for="suggestion in quickReplies"
        :key="suggestion"
        @click="useQuickReply(suggestion)"
        class="tw-px-3 tw-py-1 tw-text-xs tw-bg-white tw-border tw-border-gray-200 tw-rounded-full hover:tw-bg-primary-50 hover:tw-border-primary-200 tw-transition-all tw-duration-200 tw-text-gray-600 hover:tw-text-primary-700"
      >
        {{ suggestion }}
      </button>
    </div>
  </div>

  <!-- Schedule Meeting Popup -->
  <CompanyMeetingPopup
    v-if="showCompanyFeatures && showMeetingPopup"
    :visible="showMeetingPopup"
    :company-id="companyId"
    :company-name="companyName"
    :company-logo="companyLogo"
    @close="showMeetingPopup = false"
    @meeting-created="onMeetingCreated"
  />
</template>

<script setup>
import { ref, defineProps, defineEmits, nextTick, defineExpose, onUnmounted } from 'vue';
import realtimeMessagingService from '@/services/realtime-messaging';
import companyApi from '@/api/companies';
import CompanyMeetingPopup from '@/components/chat/CompanyMeetingPopup.vue';

// Props
const props = defineProps({
  conversationId: {
    type: String,
    required: true
  },
  disabled: {
    type: Boolean,
    default: false
  },
  // Company chat features
  showCompanyFeatures: {
    type: Boolean,
    default: false
  },
  companyId: {
    type: [String, Number],
    default: null
  },
  companyName: {
    type: String,
    default: 'Company'
  },
  companyLogo: {
    type: String,
    default: null
  }
});

// Emits
const emit = defineEmits(['message-sent', 'message-sending']);

// Reactive data
const messageText = ref('');
const sending = ref(false);
const messageInput = ref(null);
const typingTimeout = ref(null);

// Quick reply suggestions
const quickReplies = ref([
  'Hello!',
  'How are you?',
  'Thank you!',
  'I\'m interested',
  'Can we schedule a meeting?'
]);

// Auto-resize textarea
const handleInput = () => {
  if (messageInput.value) {
    messageInput.value.style.height = 'auto';
    messageInput.value.style.height = Math.min(messageInput.value.scrollHeight, 120) + 'px';
  }

  // Send typing indicator
  if (messageText.value.trim()) {
    realtimeMessagingService.sendTypingIndicator(props.conversationId, true);
    
    // Clear existing timeout
    if (typingTimeout.value) {
      clearTimeout(typingTimeout.value);
    }
    
    // Set timeout to stop typing indicator after 2 seconds
    typingTimeout.value = setTimeout(() => {
      realtimeMessagingService.sendTypingIndicator(props.conversationId, false);
    }, 2000);
  }
};

// Clear message
const clearMessage = () => {
  messageText.value = '';
  if (messageInput.value) {
    messageInput.value.style.height = 'auto';
  }
};

// Use quick reply
const useQuickReply = (reply) => {
  messageText.value = reply;
  handleInput();
  messageInput.value?.focus();
};

// Send message function
const sendMessage = async () => {
  if (!messageText.value.trim() || sending.value || props.disabled) return;
  
  const messageContent = messageText.value.trim();
  sending.value = true;
  
  try {
    // Stop typing indicator
    realtimeMessagingService.sendTypingIndicator(props.conversationId, false);
    if (typingTimeout.value) {
      clearTimeout(typingTimeout.value);
      typingTimeout.value = null;
    }
    
    // Emit sending event
    emit('message-sending', { content: messageContent });
    
    // Clear the input
    messageText.value = '';
    if (messageInput.value) {
      messageInput.value.style.height = 'auto';
    }
    
    // Emit message sent event
    emit('message-sent', { content: messageContent });
    
    // Focus back to input
    await nextTick();
    messageInput.value?.focus();
    
  } catch (error) {
    console.error('Error sending message:', error);
  } finally {
    sending.value = false;
  }
};

// ── Company features ───────────────────────────────────────────
// Schedule meeting
const showMeetingPopup = ref(false);
const openMeetingPopup = () => { showMeetingPopup.value = true; };

const onMeetingCreated = (meeting) => {
  const title = meeting?.data?.title || meeting?.title || 'meeting';
  const scheduled = meeting?.data?.scheduled_at || meeting?.scheduled_at;
  const dateStr = scheduled
    ? new Date(scheduled).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true })
    : '';
  messageText.value = `📅 I've scheduled a meeting: "${title}"${dateStr ? ` on ${dateStr}` : ''}`;
  handleInput();
  messageInput.value?.focus();
};

// Tag picker
const showTagPicker = ref(false);
const tagPickerLoading = ref(false);
const companyProductsList = ref([]);
const companyServicesList = ref([]);

const toggleTagPicker = async () => {
  showTagPicker.value = !showTagPicker.value;
  if (showTagPicker.value && companyProductsList.value.length === 0 && companyServicesList.value.length === 0) {
    await fetchCompanyTagItems();
  }
};

const fetchCompanyTagItems = async () => {
  if (!props.companyId) return;
  tagPickerLoading.value = true;
  try {
    const [productsRes, servicesRes] = await Promise.all([
      companyApi.compnayProducts(props.companyId),
      companyApi.companyServices(props.companyId)
    ]);
    companyProductsList.value = productsRes.data?.data || productsRes.data || [];
    companyServicesList.value = servicesRes.data?.data || servicesRes.data || [];
  } catch (err) {
    console.error('Error fetching company products/services:', err);
  } finally {
    tagPickerLoading.value = false;
  }
};

const appendTag = (type, name) => {
  const tag = `\n${type}: ${name}`;
  messageText.value = messageText.value.trimEnd() + tag;
  showTagPicker.value = false;
  handleInput();
  nextTick(() => messageInput.value?.focus());
};
// ───────────────────────────────────────────────────────────────

// Expose methods for parent component
defineExpose({
  clearMessage,
  focus: () => messageInput.value?.focus()
});

// Cleanup typing timeout on unmount
onUnmounted(() => {
  if (typingTimeout.value) {
    clearTimeout(typingTimeout.value);
  }
});
</script>

<style scoped>
/* Custom focus styles */
textarea:focus {
  outline: none;
}
</style> 